// Billing state of an organization's subscription. Stripe subscriptions renew
// unless `cancel_at_period_end` is set; legacy (Braintree / admin-granted)
// subscriptions use `neverExpires`.

export const isStripeSubscription = (subscription) =>
  !!subscription && !subscription.custom && String(subscription.id || '').startsWith('sub_')

/**
 * Whether the subscription lets the organization call and text right now (the
 * backend checks the same: admin-granted plans always, Stripe plans only while
 * `active` or `trialing`, legacy trials without a status). The balance of a
 * subscription that is not active cannot be used.
 */
export const isSubscriptionActive = (subscription) => {
  if (!subscription) return false
  if (subscription.type === 'admin') return true
  if (subscription.status) return ['active', 'trialing'].includes(subscription.status)
  return subscription.type === 'trial'
}

export const isAutoBillingOn = (subscription) => {
  if (!subscription) return false
  if (isStripeSubscription(subscription)) return !subscription.cancel_at_period_end
  return !!subscription.neverExpires
}

/**
 * Next billing event of a Stripe subscription: the day the plan ends when
 * automatic billing is off, otherwise the day of the next charge.
 * @returns {{ label: string, date: string } | null}
 */
export const getBillingDate = (subscription) => {
  if (!isStripeSubscription(subscription)) return null
  const trialing = subscription.status === 'trialing'
  const timestamp = subscription.cancel_at_period_end
    ? subscription.cancel_at || subscription.current_period_end
    : trialing
    ? subscription.trial_end || subscription.current_period_end
    : subscription.current_period_end
  if (!timestamp) return null
  return {
    label: subscription.cancel_at_period_end
      ? 'Plan ends on'
      : trialing
      ? 'Free trial ends on'
      : 'Next charge on',
    date: new Date(timestamp * 1000).toDateString(),
  }
}

/**
 * Whether bundles can be added to the monthly plan: a monthly Stripe plan with
 * automatic billing on (the backend checks the same).
 */
export const canAddMonthlyBundles = (subscription) => {
  if (!isStripeSubscription(subscription) || !isAutoBillingOn(subscription)) return false
  if (!['active', 'trialing'].includes(subscription.status)) return false
  const interval =
    subscription.plan?.interval || subscription.items?.data?.[0]?.price?.recurring?.interval
  return !interval || interval === 'month'
}

export const isMonthlyBundle = (bundle) =>
  !!bundle && bundle.recurringAvailable !== false && !!bundle.stripePriceId

/**
 * Recurring bundles renewed at the next billing date (removed ones excluded)
 */
export const getActiveRecurringBundles = (organization) =>
  (organization?.recurringBundles || []).filter(
    (entry) => !entry.removeAtPeriodEnd && entry.quantity > 0,
  )

/**
 * Monthly minutes, texts and price the recurring bundles add to the plan
 */
export const getRecurringBundleTotals = (organization) =>
  getActiveRecurringBundles(organization).reduce(
    (totals, entry) => ({
      minutes:
        totals.minutes + (entry.type === 'minutes' ? (entry.minutes || 0) * entry.quantity : 0),
      sms: totals.sms + (entry.type === 'sms' ? (entry.sms || 0) * entry.quantity : 0),
      price: totals.price + (entry.price || 0) * entry.quantity,
    }),
    { minutes: 0, sms: 0, price: 0 },
  )

/**
 * Date of the next renewal (when removed bundles end and carried-over balance
 * expires), or null for a plan that does not renew
 */
export const getNextRenewalDate = (subscription) => {
  if (!isStripeSubscription(subscription)) return null
  const timestamp =
    subscription.status === 'trialing'
      ? subscription.trial_end || subscription.current_period_end
      : subscription.current_period_end
  return timestamp ? new Date(timestamp * 1000).toDateString() : null
}

/**
 * Whether the backend refused a charge because no card is saved
 */
export const isPaymentMethodRequired = (error) =>
  error?.response?.status === 402 &&
  /payment method/i.test(error?.response?.data?.error?.message || '')
